import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { CalendarDays, Clock, MapPin, Building2, Check, X, Loader2 } from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function Appointments() {
  const { t } = useI18n();
  const [embassies, setEmbassies] = useState([]);
  const [selectedEmbassy, setSelectedEmbassy] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [myAppointments, setMyAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bookingDialog, setBookingDialog] = useState(false);
  const [reason, setReason] = useState('Retrait de carte');
  const [booking, setBooking] = useState(false);
  const [schedule, setSchedule] = useState(null);

  useEffect(() => {
    api.get('/embassies').then(r => setEmbassies(r.data)).catch(() => {});
    api.get('/appointments/my').then(r => setMyAppointments(r.data)).catch(() => {});
  }, []);

  // Load schedule when embassy changes
  useEffect(() => {
    if (selectedEmbassy) {
      api.get(`/embassies/${selectedEmbassy}/schedule`).then(r => setSchedule(r.data)).catch(() => {});
      setSelectedDate(null);
      setSlots([]);
      setSelectedSlot(null);
    }
  }, [selectedEmbassy]);

  // Load available slots when date changes
  const loadSlots = useCallback(async () => {
    if (!selectedEmbassy || !selectedDate) return;
    const dateStr = formatDate(selectedDate);
    setLoading(true);
    try {
      const r = await api.get(`/appointments/available-slots?embassy_id=${selectedEmbassy}&date=${dateStr}`);
      setSlots(r.data.slots || []);
    } catch {
      setSlots([]);
    } finally {
      setLoading(false);
    }
  }, [selectedEmbassy, selectedDate]);

  useEffect(() => { loadSlots(); }, [loadSlots]);

  const formatDate = (d) => {
    if (!d) return '';
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Determine which days are open based on schedule
  const isDateDisabled = (date) => {
    if (date < new Date(new Date().setHours(0, 0, 0, 0))) return true;
    if (!schedule || !schedule.slots) return false;
    const dow = date.getDay();
    const adjustedDow = dow === 0 ? 6 : dow - 1; // JS: 0=Sun, our API: 0=Mon
    return !schedule.slots.some(s => s.day_of_week === adjustedDow);
  };

  const handleBook = async () => {
    if (!selectedSlot || !selectedEmbassy || !selectedDate) return;
    setBooking(true);
    try {
      await api.post('/appointments', {
        embassy_id: selectedEmbassy,
        date: formatDate(selectedDate),
        time: selectedSlot,
        reason,
      });
      toast.success('Rendez-vous confirme !');
      setBookingDialog(false);
      setSelectedSlot(null);
      loadSlots();
      api.get('/appointments/my').then(r => setMyAppointments(r.data));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la reservation');
    } finally {
      setBooking(false);
    }
  };

  const handleCancel = async (apptId) => {
    if (!window.confirm('Annuler ce rendez-vous ?')) return;
    try {
      await api.post(`/appointments/${apptId}/cancel`);
      toast.success('Rendez-vous annule');
      api.get('/appointments/my').then(r => setMyAppointments(r.data));
    } catch {
      toast.error('Erreur');
    }
  };

  const emb = embassies.find(e => e.embassy_id === selectedEmbassy);
  const activeAppointments = myAppointments.filter(a => a.status !== 'cancelled');

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-fade-in" data-testid="appointments-page">
      <div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {t('appt.title')}
        </h1>
        <p className="text-muted-foreground text-sm mt-1">{t('appt.desc')}</p>
      </div>

      {/* Active Appointments */}
      {activeAppointments.length > 0 && (
        <Card className="border border-green-200 bg-green-50/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('appt.my_appointments')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {activeAppointments.map(appt => (
              <div key={appt.appointment_id} className="flex items-center justify-between p-3 rounded-lg bg-white border border-border">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#007FFF]/10 flex items-center justify-center shrink-0">
                    <CalendarDays className="w-5 h-5 text-[#007FFF]" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">{appt.embassy_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(appt.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })} - {appt.time}
                    </p>
                    <p className="text-xs text-muted-foreground">{appt.reason}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-green-100 text-green-800">{t('appt.confirmed')}</Badge>
                  <Button variant="ghost" size="sm" onClick={() => handleCancel(appt.appointment_id)} data-testid={`cancel-appt-${appt.appointment_id}`}>
                    <X className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Step 1: Select Embassy */}
      <Card className="border">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Building2 className="w-5 h-5 text-[#007FFF]" />
            {t('appt.select_embassy')}
          </CardTitle>
          <CardDescription>{t('appt.select_embassy_desc')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Select value={selectedEmbassy} onValueChange={setSelectedEmbassy}>
            <SelectTrigger data-testid="select-embassy"><SelectValue placeholder={t('common.choose')} /></SelectTrigger>
            <SelectContent>
              {embassies.map(e => (
                <SelectItem key={e.embassy_id} value={e.embassy_id}>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
                    {e.country} - {e.city}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {emb && (
            <div className="mt-3 p-3 rounded-lg bg-muted/50 text-sm space-y-1">
              {emb.address && <p className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5" /> {emb.address}</p>}
              {emb.phone && <p className="flex items-center gap-2"><Clock className="w-3.5 h-3.5" /> {emb.phone}</p>}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Step 2: Select Date */}
      {selectedEmbassy && (
        <Card className="border">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <CalendarDays className="w-5 h-5 text-[#007FFF]" />
              {t('appt.select_date')}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex justify-center">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={setSelectedDate}
              disabled={isDateDisabled}
              className="rounded-lg border"
              data-testid="appointment-calendar"
            />
          </CardContent>
        </Card>
      )}

      {/* Step 3: Select Time Slot */}
      {selectedDate && (
        <Card className="border">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Clock className="w-5 h-5 text-[#007FFF]" />
              {t('appt.select_time')} - {selectedDate.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#007FFF]" /></div>
            ) : slots.length === 0 ? (
              <p className="text-center py-8 text-muted-foreground">{t('appt.no_slots')}</p>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                {slots.map(slot => (
                  <button
                    key={slot.time}
                    onClick={() => { if (slot.available) { setSelectedSlot(slot.time); setBookingDialog(true); } }}
                    disabled={!slot.available}
                    className={`p-2.5 rounded-lg border text-sm font-medium transition-all ${
                      slot.available
                        ? 'border-[#007FFF]/30 hover:bg-[#007FFF] hover:text-white hover:border-[#007FFF] cursor-pointer'
                        : 'border-border bg-muted/50 text-muted-foreground cursor-not-allowed line-through'
                    } ${selectedSlot === slot.time ? 'bg-[#007FFF] text-white border-[#007FFF]' : ''}`}
                    data-testid={`slot-${slot.time}`}
                  >
                    {slot.time}
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Booking Confirmation Dialog */}
      <Dialog open={bookingDialog} onOpenChange={setBookingDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>{t('appt.confirm_title')}</DialogTitle>
            <p className="text-sm text-muted-foreground">Verifiez les details de votre rendez-vous</p>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 rounded-lg bg-[#007FFF]/5 border border-[#007FFF]/20 space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <Building2 className="w-4 h-4 text-[#007FFF]" />
                <span className="font-medium">{emb?.country} - {emb?.city}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <CalendarDays className="w-4 h-4 text-[#007FFF]" />
                <span>{selectedDate?.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Clock className="w-4 h-4 text-[#007FFF]" />
                <span className="font-medium text-lg">{selectedSlot}</span>
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('appt.reason')}</Label>
              <Input value={reason} onChange={e => setReason(e.target.value)} data-testid="appointment-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBookingDialog(false)}>{t('common.cancel')}</Button>
            <Button className="bg-[#009543] hover:bg-[#007A36]" onClick={handleBook} disabled={booking} data-testid="confirm-booking-btn">
              {booking ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              {t('appt.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
